import type { AppState } from '../state/store';
import type { EquipSlot, InventoryItem } from '../api/types';
import { compatibleSlots, describeItem, rarityLabel, slotLabel } from '../inventory/format';
import { btn, esc, pill } from './components';
import { ownedCosmeticsCollectionCard } from './cosmeticsView';
import { itemIcon } from './icons';

const ALL_SLOTS: EquipSlot[] = ['weapon', 'armor', 'trinket1', 'trinket2', 'lantern'];
const MIN_STASH_SLOTS = 20;

function equipmentPanel(state: AppState): string {
  const equipped = new Map<EquipSlot, InventoryItem>();
  for (const it of state.equipment?.items ?? []) {
    if (it.equippedSlot) equipped.set(it.equippedSlot, it);
  }
  const slots = ALL_SLOTS.map((slot) => {
    const it = equipped.get(slot);
    if (!it) return `<div class="slot slot--empty"><span class="slot__name">${esc(slotLabel(slot))}</span><span class="muted">empty</span></div>`;
    const v = describeItem(it.item);
    return `
      <div class="slot">
        <span class="slot__name">${esc(slotLabel(slot))}</span>
        <span class="item-title">${itemIcon(it.item.slotType)}<span class="rarity rarity--${esc(it.item.rarity)}">${esc(v.title)}</span></span>
        <span class="slot__meta">${esc(v.affixes.join(' · ') || 'no affixes')} · dura ${esc(v.durability)}</span>
        ${btn('Unequip', 'unequip', { slot }, { variant: 'ghost' })}
      </div>`;
  }).join('');
  const fx = state.equipment?.effects;
  const effects = fx
    ? `<p class="hint">Effects → Max HP +${esc(fx.maxHp)} · ATK +${esc(fx.attack)} · DEF +${esc(fx.defense)} · Lantern drain -${esc((fx.lanternEfficiencyBps / 100).toFixed(1).replace(/\.0$/, ''))}% · Burden load -${esc(fx.burdenReduction)}</p>`
    : '';
  return `
    <section class="card card--wide">
      <h2>Equipment</h2>
      <div class="slots">${slots}</div>
      ${effects}
    </section>`;
}

function equipButtons(it: InventoryItem): string {
  return compatibleSlots(it.item.slotType)
    .map((slot) => btn(
      `Equip → ${slotLabel(slot)}`,
      'equip',
      { item: it.itemInstanceId, slot },
      { variant: 'ghost' },
    ))
    .join('');
}

function selectedItemDetails(it: InventoryItem): string {
  const v = describeItem(it.item);
  const location = it.equippedSlot ? `Equipment — ${slotLabel(it.equippedSlot)}` : 'Stash';
  let actions: string;
  if (it.equippedSlot) {
    actions = `${pill(`equipped: ${slotLabel(it.equippedSlot)}`, 'ok')} ${btn('Unequip', 'unequip', { slot: it.equippedSlot }, { variant: 'ghost' })}`;
  } else if (it.item.sealState !== 'sealed') {
    actions = pill('unsealed — cannot equip', 'warn');
  } else if (v.broken) {
    actions = pill('broken — repair needed', 'down');
  } else {
    actions = equipButtons(it);
  }
  return `
    <div class="item item--selected-detail" data-selected-item-details="${esc(it.itemInstanceId)}">
      <div class="item__head">
        ${itemIcon(it.item.slotType)}
        <span class="rarity rarity--${esc(it.item.rarity)}">${esc(v.title)}</span>
        ${v.flags.map((f) => pill(f, 'muted')).join(' ')}
      </div>
      <div class="item__meta">${esc(v.affixes.join(' · ') || 'no affixes')}</div>
      <div class="item__meta"><strong>Location</strong> ${esc(location)}</div>
      <div class="item__meta">dura ${esc(v.durability)} · ${esc(v.sealText)} · <code>${esc(it.itemInstanceId.slice(0, 8))}</code></div>
      <div class="btn-row">${actions}</div>
    </div>`;
}

function durabilityPct(it: InventoryItem): string {
  const max = it.item.durabilityMax;
  if (max <= 0) return '0%';
  return `${Math.max(0, Math.min(100, Math.round((it.item.durabilityCur / max) * 100)))}%`;
}

function stashBadges(it: InventoryItem): string {
  const badges = [
    { label: it.item.sealState === 'sealed' ? 'Sealed' : 'Unsealed', text: it.item.sealState === 'sealed' ? 'S' : 'U' },
    it.item.accountBound ? { label: 'Account-bound', text: 'B' } : null,
    it.equippedSlot ? { label: `Equipped in ${slotLabel(it.equippedSlot)}`, text: 'E' } : null,
  ].filter((badge): badge is { label: string; text: string } => !!badge);
  return badges.map((badge) => `<span title="${esc(badge.label)}" aria-label="${esc(badge.label)}">${esc(badge.text)}</span>`).join('');
}

function stashSlot(it: InventoryItem | undefined, index: number, selectedId: string | null): string {
  if (!it) {
    return `
      <div class="stash-slot stash-slot--empty" data-inventory-slot="${index + 1}" aria-label="Empty inventory slot">
        <span class="stash-slot__empty-mark" aria-hidden="true"></span>
      </div>`;
  }
  const v = describeItem(it.item);
  const selected = it.itemInstanceId === selectedId;
  const equipped = !!it.equippedSlot;
  const location = it.equippedSlot ? `equipped in ${slotLabel(it.equippedSlot)}` : 'in stash';
  const label = `${v.title}, ${location}, durability ${v.durability}, ${it.item.sealState}`;
  return `
    <button type="button" class="stash-slot stash-slot--filled stash-slot--${esc(it.item.rarity)}${selected ? ' stash-slot--selected' : ''}${equipped ? ' stash-slot--equipped' : ''}" data-inventory-slot="${index + 1}" data-item-id="${esc(it.itemInstanceId)}" data-source="${esc(it.item.source)}" data-act="select-inventory-item" data-item="${esc(it.itemInstanceId)}" aria-label="${esc(label)}" aria-pressed="${selected ? 'true' : 'false'}" title="${esc(label)}">
      <span class="stash-slot__rarity-chip stash-slot__rarity-chip--${esc(it.item.rarity)}" title="${esc(rarityLabel(it.item.rarity))} rarity">${esc(rarityLabel(it.item.rarity).slice(0, 1))}</span>
      <div class="stash-slot__icon">${itemIcon(it.item.slotType)}</div>
      <span class="stash-slot__durability" title="${esc(`Durability ${v.durability}`)}">
        <span class="stash-slot__durability-bar" aria-hidden="true"><span style="width: ${esc(durabilityPct(it))}"></span></span>
        <span class="stash-slot__durability-text">${esc(v.durability)}</span>
      </span>
      <span class="stash-slot__badges">${stashBadges(it)}</span>
    </button>`;
}

function stashGrid(items: InventoryItem[], selectedId: string | null): string {
  const slotCount = Math.max(MIN_STASH_SLOTS, items.length);
  return `<div class="stash-grid" role="list" aria-label="Stash slots">${Array.from({ length: slotCount }, (_, index) => stashSlot(items[index], index, selectedId)).join('')}</div>`;
}

export function renderInventory(state: AppState): string {
  if (!state.session) {
    return `<section class="card"><h2>Inventory</h2><p class="muted">Connect a wallet to view your stash.</p></section>`;
  }
  const items = state.inventory ?? [];
  const selectedId =
    state.selectedInventoryItemId && items.some((entry) => entry.itemInstanceId === state.selectedInventoryItemId)
      ? state.selectedInventoryItemId
      : items[0]?.itemInstanceId ?? null;
  const selectedItem = selectedId ? items.find((entry) => entry.itemInstanceId === selectedId) : undefined;
  const details = items.length > 0
    ? `<div class="inventory-actions"><h3>Item details</h3>${selectedItem ? selectedItemDetails(selectedItem) : '<p class="muted">Select an item to inspect.</p>'}</div>`
    : '<p class="muted">Empty stash. Extract a Reward run at/above an item\'s seal depth to mint it here.</p>';
  return `
    <div class="grid">
      ${equipmentPanel(state)}
      <section class="card card--wide">
        <div class="run__head">
          <h2>Stash</h2>
          <div class="btn-row">
            ${btn('Refresh', 'load-inventory', {}, { variant: 'ghost' })}
            ${btn('Back to Waystation', 'goto-waystation', {}, { variant: 'ghost' })}
          </div>
        </div>
        ${stashGrid(items, selectedId)}
        ${details}
      </section>
      ${cosmeticsCollectionSection(state)}
    </div>`;
}

/** Owned cosmetics under the Stash. Item inventory stays usable when this fails. */
function cosmeticsCollectionSection(state: AppState): string {
  if (state.myCosmetics === null) {
    const message = state.inventoryCosmeticsError ?? 'Cosmetics collection is not loaded yet.';
    return `
      <section class="card card--wide cosmetics-collection-card">
        <h2>Collection</h2>
        <p class="muted">${esc(message)}</p>
        <div class="btn-row">${btn('Retry', 'load-inventory', {}, { variant: 'ghost' })}</div>
      </section>`;
  }
  return ownedCosmeticsCollectionCard(state);
}
